import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../services/AuthContext';
import { Search, Edit, ArrowRightLeft, X, CheckCircle2, XCircle, Download, GraduationCap, Plus, KeyRound, FolderOpen, Trash2, UserMinus, UserCheck, FileText, ExternalLink } from 'lucide-react';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const EMPTY_EMPLOYEE = {
  first_name: '', last_name: '', dni: '', social_security_number: '',
  email: '', restaurant: '', employee_number: '', category: '', start_date: '',
};

export default function AdminEmployees() {
  const { isSuperUser } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ restaurant: '', category: '', is_active: 'true', search: '' });
  const [editModal, setEditModal] = useState(null);
  const [transferModal, setTransferModal] = useState(null);
  const [coursesModal, setCoursesModal] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [docsModal, setDocsModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Sort by category rank (descending = higher rank first), then alphabetical by surname.
  // Active employees first, then exempleados.
  const sortedEmployees = [...employees].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    const rankA = a.category_rank || 0;
    const rankB = b.category_rank || 0;
    if (rankA !== rankB) return rankB - rankA;
    const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
    const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
    return nameA.localeCompare(nameB, 'es');
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.restaurant) params.restaurant = filter.restaurant;
      if (filter.category) params.category = filter.category;
      if (filter.is_active) params.is_active = filter.is_active === 'true' ? 'True' : 'False';
      if (filter.search) params.search = filter.search;
      const [empRes, restRes, catRes] = await Promise.all([
        adminApi.getEmployees(params),
        adminApi.getRestaurants(),
        adminApi.getCategories(),
      ]);
      setEmployees(empRes.data.results || empRes.data);
      setRestaurants(restRes.data.results || restRes.data);
      setCategories(catRes.data.results || catRes.data);
    } catch { toast.error('Error al cargar empleados.'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleEdit = async () => {
    if (!editModal) return;
    try {
      await adminApi.updateEmployee(editModal.id, {
        first_name: editModal.first_name,
        last_name: editModal.last_name,
        email: editModal.email,
        category: editModal.category || null,
        start_date: editModal.start_date || null,
        is_active: editModal.is_active,
      });
      toast.success('Empleado actualizado.');
      setEditModal(null);
      load();
    } catch { toast.error('Error al actualizar.'); }
  };

  const openDocuments = async (emp) => {
    setDocsModal({ emp, loading: true, data: null });
    try {
      const res = await adminApi.getEmployeeDocuments(emp.id);
      setDocsModal({ emp, loading: false, data: res.data });
    } catch {
      toast.error('Error al cargar la documentación.');
      setDocsModal(null);
    }
  };

  const handleToggleActive = async (emp) => {
    try {
      await adminApi.setEmployeeActive(emp.id, !emp.is_active);
      toast.success(emp.is_active ? 'Empleado desactivado (exempleado).' : 'Empleado reactivado.');
      load();
    } catch { toast.error('Error al cambiar el estado.'); }
  };

  // Borrado definitivo: elimina también solicitudes y archivos. Solo
  // superusuarios; se usa para limpiar datos de prueba.
  const handlePurge = async () => {
    if (!deleteModal) return;
    try {
      const res = await adminApi.purgeEmployee(deleteModal.id);
      toast.success(res.data?.mensaje || 'Empleado eliminado definitivamente.');
      setDeleteModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar.');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await adminApi.deleteEmployee(deleteModal.id);
      toast.success('Empleado borrado. Sus archivos se conservan.');
      setDeleteModal(null);
      load();
    } catch { toast.error('Error al borrar.'); }
  };

  const handleTransfer = async () => {
    if (!transferModal) return;
    try {
      await adminApi.transferEmployee(transferModal.id, {
        restaurant: transferModal.new_restaurant,
        employee_number: transferModal.new_number,
      });
      toast.success('Traslado realizado.');
      setTransferModal(null);
      load();
    } catch { toast.error('Error al trasladar.'); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filter.restaurant) params.restaurant = filter.restaurant;
      if (filter.category) params.category = filter.category;
      if (filter.is_active) params.is_active = filter.is_active === 'true' ? 'True' : 'False';
      const res = await adminApi.exportEmployees(params);
      downloadBlob(new Blob([res.data]), 'empleados_saeweb.xlsx');
      toast.success('Excel descargado.');
    } catch { toast.error('Error al exportar.'); }
    setExporting(false);
  };

  const openCourses = async (empId, empName) => {
    setCoursesModal({ loading: true, data: null, name: empName });
    try {
      const res = await adminApi.getEmployeeCourses(empId);
      setCoursesModal({ loading: false, data: res.data, name: empName });
    } catch {
      toast.error('Error al cargar cursos.');
      setCoursesModal(null);
    }
  };

  // ─── Create ────────────────────────────
  const handleCreate = async () => {
    if (!createModal) return;
    const { first_name, last_name, dni, restaurant, employee_number } = createModal;
    if (!first_name || !last_name || !dni || !restaurant || !employee_number) {
      toast.error('Nombre, apellidos, DNI, restaurante y nº empleado son obligatorios.');
      return;
    }
    try {
      const payload = { ...createModal };
      // Clean empty strings to null for optional fields
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      // These must not be null
      payload.first_name = createModal.first_name;
      payload.last_name = createModal.last_name;
      payload.dni = createModal.dni;
      payload.employee_number = parseInt(createModal.employee_number);
      await adminApi.createEmployee(payload);
      toast.success('Empleado creado correctamente.');
      setCreateModal(null);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msgs = Object.values(data).flat().join(' ');
        toast.error(msgs || 'Error al crear empleado.');
      } else {
        toast.error('Error al crear empleado.');
      }
    }
  };

  // ─── Set Credentials ───────────────────
  const handleSetCredentials = async () => {
    if (!credentialsModal?.password || credentialsModal.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    try {
      await adminApi.setEmployeeCredentials(credentialsModal.id, {
        password: credentialsModal.password,
        email: credentialsModal.email || '',
      });
      toast.success('Credenciales establecidas correctamente.');
      setCredentialsModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al establecer credenciales.');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Empleados</h1>
          <p>Gestiona los empleados de la franquicia</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primary" onClick={() => setCreateModal({ ...EMPTY_EMPLOYEE })}>
            <Plus size={16} />
            Nuevo empleado
          </button>
          <button className="btn btn--secondary" onClick={handleExport} disabled={exporting}>
            <Download size={16} />
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--gray-400)' }} />
          <input
            className="form-input"
            placeholder="Buscar..."
            value={filter.search}
            onChange={e => setFilter({ ...filter, search: e.target.value })}
            style={{ paddingLeft: 32 }}
          />
        </div>
        <select className="form-input" value={filter.restaurant} onChange={e => setFilter({ ...filter, restaurant: e.target.value })}>
          <option value="">Todos los restaurantes</option>
          {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select className="form-input" value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })}>
          <option value="">Todas las categorías</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-input" value={filter.is_active} onChange={e => setFilter({ ...filter, is_active: e.target.value })}>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
          <option value="">Todos</option>
        </select>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Restaurante</th>
                <th>Categoría</th>
                <th>Estado</th>
                <th>Cursos OK</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedEmployees.length === 0 ? (
                <tr><td colSpan={8} className="empty-state"><p>No se encontraron empleados</p></td></tr>
              ) : sortedEmployees.map(emp => (
                <tr key={emp.id} style={emp.is_active ? undefined : { opacity: 0.6 }}>
                  <td><span className="badge badge--info">{emp.employee_code}</span></td>
                  <td>{emp.first_name} {emp.last_name}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{emp.email || '—'}</td>
                  <td>{emp.restaurant_name || '—'}</td>
                  <td>{emp.category_name || '—'}</td>
                  <td>
                    {emp.is_active
                      ? <span className="badge badge--success" style={{ fontSize: '0.7rem' }}>Activo</span>
                      : <span className="badge" style={{ fontSize: '0.7rem', background: 'var(--gray-200)', color: 'var(--gray-600)' }}>Exempleado</span>}
                  </td>
                  <td>
                    {emp.courses_ok
                      ? <CheckCircle2 size={16} color="var(--success)" />
                      : <XCircle size={16} color="var(--danger)" />
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn--ghost btn--sm" title="Ver documentación" onClick={() => openDocuments(emp)}>
                        <FolderOpen size={14} />
                      </button>
                      <button className="btn btn--ghost btn--sm" title="Ver cursos" onClick={() => openCourses(emp.id, `${emp.first_name} ${emp.last_name}`)}>
                        <GraduationCap size={14} />
                      </button>
                      <button className="btn btn--ghost btn--sm" title="Editar" onClick={() => setEditModal({ ...emp })}>
                        <Edit size={14} />
                      </button>
                      <button className="btn btn--ghost btn--sm" title="Trasladar" onClick={() => setTransferModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}`, new_restaurant: '', new_number: '' })}>
                        <ArrowRightLeft size={14} />
                      </button>
                      <button className="btn btn--ghost btn--sm" title="Credenciales" onClick={() => setCredentialsModal({ id: emp.id, name: `${emp.first_name} ${emp.last_name}`, code: emp.employee_code, password: '', email: emp.email || '' })}>
                        <KeyRound size={14} />
                      </button>
                      <button className="btn btn--ghost btn--sm" title={emp.is_active ? 'Desactivar (exempleado)' : 'Reactivar'}
                        onClick={() => handleToggleActive(emp)}>
                        {emp.is_active ? <UserMinus size={14} color="var(--amber-500)" /> : <UserCheck size={14} color="var(--success)" />}
                      </button>
                      <button className="btn btn--ghost btn--sm" title="Borrar (conserva archivos)" onClick={() => setDeleteModal(emp)}>
                        <Trash2 size={14} color="var(--danger)" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ EDIT MODAL ═══ */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Editar empleado</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setEditModal(null)}><X size={18} /></button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre</label>
                <input className="form-input" value={editModal.first_name} onChange={e => setEditModal({ ...editModal, first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Apellidos</label>
                <input className="form-input" value={editModal.last_name} onChange={e => setEditModal({ ...editModal, last_name: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input className="form-input" type="email" value={editModal.email || ''} onChange={e => setEditModal({ ...editModal, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Categoría profesional</label>
              <select className="form-input" value={editModal.category || ''} onChange={e => setEditModal({ ...editModal, category: e.target.value || null })}>
                <option value="">Sin categoría</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fecha inicio</label>
                <input className="form-input" type="date" value={editModal.start_date || ''} onChange={e => setEditModal({ ...editModal, start_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Activo</label>
                <select className="form-input" value={editModal.is_active ? 'true' : 'false'} onChange={e => setEditModal({ ...editModal, is_active: e.target.value === 'true' })}>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setEditModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleEdit}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TRANSFER MODAL ═══ */}
      {transferModal && (
        <div className="modal-overlay" onClick={() => setTransferModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Trasladar a {transferModal.name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setTransferModal(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label>Nuevo restaurante</label>
              <select className="form-input" value={transferModal.new_restaurant} onChange={e => setTransferModal({ ...transferModal, new_restaurant: e.target.value })}>
                <option value="">Seleccionar</option>
                {restaurants.map(r => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Nuevo número de empleado (opcional)</label>
              <input className="form-input" type="number" value={transferModal.new_number} onChange={e => setTransferModal({ ...transferModal, new_number: e.target.value })} placeholder="Si cambia de número" />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setTransferModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleTransfer} disabled={!transferModal.new_restaurant}>Trasladar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ COURSES DETAIL MODAL ═══ */}
      {coursesModal && (
        <div className="modal-overlay" onClick={() => setCoursesModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal__header">
              <h3>Cursos de {coursesModal.name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCoursesModal(null)}><X size={18} /></button>
            </div>

            {coursesModal.loading ? (
              <div className="spinner" />
            ) : coursesModal.data ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span className="badge badge--info">{coursesModal.data.employee.employee_code}</span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    {coursesModal.data.employee.restaurant} · {coursesModal.data.employee.category}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  <div className="stat-card" style={{ flex: 1, padding: 12, textAlign: 'center' }}>
                    <div className="stat-card__value" style={{ fontSize: '1.4rem' }}>{coursesModal.data.summary.total}</div>
                    <div className="stat-card__label">Asignados</div>
                  </div>
                  <div className="stat-card" style={{ flex: 1, padding: 12, textAlign: 'center' }}>
                    <div className="stat-card__value" style={{ fontSize: '1.4rem', color: 'var(--success)' }}>{coursesModal.data.summary.completed}</div>
                    <div className="stat-card__label">Completados</div>
                  </div>
                  <div className="stat-card" style={{ flex: 1, padding: 12, textAlign: 'center' }}>
                    <div className="stat-card__value" style={{ fontSize: '1.4rem', color: 'var(--amber-500)' }}>{coursesModal.data.summary.pending}</div>
                    <div className="stat-card__label">Pendientes</div>
                  </div>
                  <div className="stat-card" style={{ flex: 1, padding: 12, textAlign: 'center' }}>
                    <div className="stat-card__value" style={{ fontSize: '1.4rem', color: 'var(--danger)' }}>{coursesModal.data.summary.overdue}</div>
                    <div className="stat-card__label">Vencidos</div>
                  </div>
                </div>

                {coursesModal.data.courses.length === 0 ? (
                  <div className="empty-state"><p>No tiene cursos asignados</p></div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Curso</th>
                        <th>Fecha límite</th>
                        <th>Asignado por</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coursesModal.data.courses.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>
                            {c.course_name}
                            {c.course_mandatory && <span className="badge badge--danger" style={{ marginLeft: 6, fontSize: '0.65rem' }}>Oblig.</span>}
                          </td>
                          <td style={{ color: c.is_overdue ? 'var(--danger)' : 'inherit', fontWeight: c.is_overdue ? 600 : 400 }}>
                            {new Date(c.deadline).toLocaleDateString('es-ES')}
                            {c.is_overdue && ' ⚠'}
                          </td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{c.assigned_via}</td>
                          <td>
                            {c.completed ? (
                              <span className="badge badge--success">
                                Completado {c.completed_date ? `(${new Date(c.completed_date).toLocaleDateString('es-ES')})` : ''}
                              </span>
                            ) : c.is_overdue ? (
                              <span className="badge badge--danger">Vencido</span>
                            ) : (
                              <span className="badge badge--warning">Pendiente</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ═══ CREATE EMPLOYEE MODAL ═══ */}
      {createModal && (
        <div className="modal-overlay" onClick={() => setCreateModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal__header">
              <h3>Nuevo empleado</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCreateModal(null)}><X size={18} /></button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nombre *</label>
                <input className="form-input" value={createModal.first_name} onChange={e => setCreateModal({ ...createModal, first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Apellidos *</label>
                <input className="form-input" value={createModal.last_name} onChange={e => setCreateModal({ ...createModal, last_name: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>DNI *</label>
                <input className="form-input" value={createModal.dni} onChange={e => setCreateModal({ ...createModal, dni: e.target.value })} placeholder="12345678A" />
              </div>
              <div className="form-group">
                <label>Nº Seguridad Social</label>
                <input className="form-input" value={createModal.social_security_number} onChange={e => setCreateModal({ ...createModal, social_security_number: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Email</label>
              <input className="form-input" type="email" value={createModal.email} onChange={e => setCreateModal({ ...createModal, email: e.target.value })} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Restaurante *</label>
                <select className="form-input" value={createModal.restaurant} onChange={e => setCreateModal({ ...createModal, restaurant: e.target.value })}>
                  <option value="">Seleccionar</option>
                  {restaurants.map(r => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Nº Empleado *</label>
                <input className="form-input" type="number" value={createModal.employee_number} onChange={e => setCreateModal({ ...createModal, employee_number: e.target.value })} placeholder="Ej: 15" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Categoría profesional</label>
                <select className="form-input" value={createModal.category} onChange={e => setCreateModal({ ...createModal, category: e.target.value })}>
                  <option value="">Sin categoría</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Fecha de inicio</label>
                <input className="form-input" type="date" value={createModal.start_date} onChange={e => setCreateModal({ ...createModal, start_date: e.target.value })} />
              </div>
            </div>

            <div className="alert alert--info" style={{ fontSize: '0.82rem', marginTop: 8 }}>
              El código del empleado se generará automáticamente combinando el código del restaurante y el número de empleado.
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setCreateModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleCreate}>
                <Plus size={16} /> Crear empleado
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CREDENTIALS MODAL ═══ */}
      {credentialsModal && (
        <div className="modal-overlay" onClick={() => setCredentialsModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal__header">
              <h3>Credenciales de {credentialsModal.name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setCredentialsModal(null)}><X size={18} /></button>
            </div>

            <div className="alert alert--info" style={{ fontSize: '0.84rem' }}>
              Código de usuario: <strong>{credentialsModal.code}</strong>
              <br />
              Establece una nueva contraseña para este empleado. No podrás ver la contraseña actual.
            </div>

            <div className="form-group">
              <label>Email (opcional, para recuperación)</label>
              <input className="form-input" type="email" value={credentialsModal.email} onChange={e => setCredentialsModal({ ...credentialsModal, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Nueva contraseña (mín. 8 caracteres) *</label>
              <input className="form-input" type="password" value={credentialsModal.password} onChange={e => setCredentialsModal({ ...credentialsModal, password: e.target.value })} placeholder="Mínimo 8 caracteres" />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setCredentialsModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleSetCredentials}>
                <KeyRound size={16} /> Establecer credenciales
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DOCUMENTS MODAL ═══ */}
      {docsModal && (
        <div className="modal-overlay" onClick={() => setDocsModal(null)}>
          <div className="modal modal--lg" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Documentación de {docsModal.emp.first_name} {docsModal.emp.last_name}</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setDocsModal(null)}><X size={18} /></button>
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: '0.85rem' }}>
              <div><span style={{ color: 'var(--gray-500)' }}>Código:</span> <strong>{docsModal.emp.employee_code}</strong></div>
              <div><span style={{ color: 'var(--gray-500)' }}>DNI:</span> <strong>{docsModal.emp.dni}</strong></div>
              <div><span style={{ color: 'var(--gray-500)' }}>Email:</span> <strong>{docsModal.emp.email || '—'}</strong></div>
              <div><span style={{ color: 'var(--gray-500)' }}>Restaurante:</span> <strong>{docsModal.emp.restaurant_name || '—'}</strong></div>
              <div><span style={{ color: 'var(--gray-500)' }}>Categoría:</span> <strong>{docsModal.emp.category_name || '—'}</strong></div>
            </div>

            {docsModal.loading ? <div className="spinner" /> : (
              (() => {
                const files = docsModal.data?.files || [];
                if (files.length === 0) {
                  return <div className="empty-state"><p>Este empleado no tiene documentación subida todavía.</p></div>;
                }
                const groups = {
                  gestoria: files.filter(f => f.source === 'gestoria'),
                  employee: files.filter(f => f.source === 'employee'),
                  admin: files.filter(f => f.source === 'admin'),
                };
                const groupLabels = {
                  gestoria: 'Documentos de gestoría (contrato, anexo, modelo 145)',
                  employee: 'Documentos firmados por el empleado',
                  admin: 'Documentos de administración',
                };
                const isImage = (url) => /\.(png|jpe?g|gif|webp)$/i.test(url || '');
                return ['gestoria', 'employee', 'admin'].map(key => {
                  if (groups[key].length === 0) return null;
                  return (
                    <div key={key} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {groupLabels[key]}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                        {groups[key].map(f => (
                          <a key={f.id} href={f.file} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', textDecoration: 'none', color: 'inherit' }}>
                            <div style={{ height: 120, background: 'var(--gray-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              {isImage(f.file)
                                ? <img src={f.file} alt={f.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <FileText size={42} color="var(--blue-400)" />}
                            </div>
                            <div style={{ padding: '8px 10px' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 500, marginBottom: 2 }}>{f.label}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <ExternalLink size={11} /> Abrir · {new Date(f.uploaded_at).toLocaleDateString('es-ES')}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn--secondary" onClick={() => setDocsModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Borrar empleado</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setDeleteModal(null)}><X size={18} /></button>
            </div>
            <p style={{ marginBottom: 8 }}>
              ¿Seguro que quieres borrar a <strong>{deleteModal.first_name} {deleteModal.last_name}</strong> ({deleteModal.employee_code})?
            </p>
            <div className="alert alert--info" style={{ marginBottom: 16 }}>
              Se eliminará el empleado y su acceso, pero <strong>sus archivos subidos se conservarán</strong>.
              Si solo quieres que pase a exempleados conservando su acceso a la lista, usa "Desactivar" en su lugar.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn--secondary" onClick={() => setDeleteModal(null)}>Cancelar</button>
              <button className="btn" style={{ background: 'var(--danger)', color: 'white' }} onClick={handleDelete}>
                <Trash2 size={16} /> Borrar empleado
              </button>
              {isSuperUser && (
                <button className="btn"
                  style={{ background: 'var(--gray-800)', color: 'white' }}
                  onClick={() => {
                    if (window.confirm(
                      'Se borrará TODO de forma definitiva: la ficha, su acceso, '
                      + 'sus solicitudes de alta y los archivos subidos. '
                      + 'Esta acción no se puede deshacer.\n\n¿Continuar?'
                    )) handlePurge();
                  }}>
                  <Trash2 size={16} /> Eliminar todo
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}